// src/components/RequestsList.js

import React, { useState, useEffect } from 'react';
import { db, auth } from '../config/firebase';
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Button, CircularProgress } from '@mui/material';

const RequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const requestsQuery = query(
      collection(db, 'connection_requests'),
      where('receiverId', '==', currentUser.uid),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(requestsQuery, async (querySnapshot) => {
      const userPromises = querySnapshot.docs.map(reqDoc => {
        const reqData = { id: reqDoc.id, ...reqDoc.data() };
        return getDoc(doc(db, 'users', reqData.senderId)).then(userDoc => ({
          ...reqData,
          sender: userDoc.data()
        }));
      });
      const enrichedRequests = await Promise.all(userPromises);
      setRequests(enrichedRequests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAccept = async (requestId) => {
    const requestRef = doc(db, 'connection_requests', requestId);
    try {
      await updateDoc(requestRef, { status: 'accepted' });
    } catch (error) {
      console.error("Error accepting request:", error);
    }
  };

  const handleDecline = async (requestId) => {
    const requestRef = doc(db, 'connection_requests', requestId);
    try {
      await updateDoc(requestRef, { status: 'declined' });
    } catch (error) {
      console.error("Error declining request:", error);
    }
  };

  if (loading) {
    return <CircularProgress sx={{ mt: 4, display:'block', margin: 'auto' }}/>;
  }

  if (requests.length === 0) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }} color="text.secondary">No pending requests.</Typography>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Pending Requests</Typography>
      <List>
        {requests.map((request) => (
          <ListItem 
            key={request.id}
            secondaryAction={
              <Box>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="small" 
                  onClick={() => handleAccept(request.id)}
                  sx={{ mr: 1 }}
                >
                  Accept
                </Button>
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small" 
                  onClick={() => handleDecline(request.id)}
                >
                  Decline
                </Button>
              </Box>
            }
          >
            <ListItemAvatar>
              <Avatar src={request.sender?.photoURL} /> 
            </ListItemAvatar>
            <ListItemText primary={request.sender?.displayName} secondary={request.sender?.email} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default RequestsList;